import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Download, Heart, Settings, Package } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import Button from '../components/common/Button';

const CustomerAccountPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer, updateProfile } = useCustomerAuth();
  const { theme } = useGalleryTheme();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  if (!customer) {
    return <div>Loading...</div>;
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'orders', label: 'Order History', icon: Package },
    { id: 'downloads', label: 'Downloads', icon: Download },
    { id: 'favorites', label: 'Favorites', icon: Heart },
    { id: 'preferences', label: 'Preferences', icon: Settings },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
          My Account
        </h1>
        <p style={{ color: theme.colors.text.secondary }}>
          Manage your profile, orders, and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <nav
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            <div className="space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors ${
                      activeTab === tab.id ? 'bg-opacity-10' : 'hover:bg-gray-100'
                    }`}
                    style={{
                      backgroundColor:
                        activeTab === tab.id ? theme.colors.primary + '10' : 'transparent',
                      color:
                        activeTab === tab.id ? theme.colors.primary : theme.colors.text.secondary,
                    }}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div
            className="p-6 rounded-lg border"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            {activeTab === 'profile' && (
              <ProfileTab customer={customer} updateProfile={updateProfile} />
            )}
            {activeTab === 'orders' && <OrdersTab />}
            {activeTab === 'downloads' && <DownloadsTab />}
            {activeTab === 'favorites' && <FavoritesTab />}
            {activeTab === 'preferences' && <PreferencesTab customer={customer} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Tab Component
const ProfileTab: React.FC<{ customer: any; updateProfile: any }> = ({
  customer,
  updateProfile,
}) => {
  const { theme } = useGalleryTheme();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: customer.name || '',
    email: customer.email || '',
    phone: customer.phone || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile(formData);
      setEditing(false);
    } catch {
      // Error handled via UI state
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
          Profile Information
        </h2>
        <Button variant={editing ? 'secondary' : 'primary'} onClick={() => setEditing(!editing)}>
          {editing ? 'Cancel' : 'Edit Profile'}
        </Button>
      </div>

      {editing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{
                borderColor: theme.colors.border,
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{
                borderColor: theme.colors.border,
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{
                borderColor: theme.colors.border,
              }}
            />
          </div>
          <Button type="submit">Save Changes</Button>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <p style={{ color: theme.colors.text.secondary }}>{customer.name || 'Not provided'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <p style={{ color: theme.colors.text.secondary }}>{customer.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <p style={{ color: theme.colors.text.secondary }}>{customer.phone || 'Not provided'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Member Since</label>
            <p style={{ color: theme.colors.text.secondary }}>
              {new Date(customer.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Orders Tab Component
const OrdersTab: React.FC = () => {
  const { theme } = useGalleryTheme();

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6" style={{ color: theme.colors.text.primary }}>
        Order History
      </h2>
      <div className="text-center py-12">
        <Package size={64} className="mx-auto mb-4" style={{ color: theme.colors.text.muted }} />
        <p style={{ color: theme.colors.text.secondary }}>
          No orders yet. Start browsing galleries to make your first purchase!
        </p>
      </div>
    </div>
  );
};

// Downloads Tab Component
const DownloadsTab: React.FC = () => {
  const { theme } = useGalleryTheme();

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6" style={{ color: theme.colors.text.primary }}>
        Downloads
      </h2>
      <div className="text-center py-12">
        <Download size={64} className="mx-auto mb-4" style={{ color: theme.colors.text.muted }} />
        <p style={{ color: theme.colors.text.secondary }}>
          Your photo downloads will appear here after purchase.
        </p>
      </div>
    </div>
  );
};

// Favorites Tab Component
const FavoritesTab: React.FC = () => {
  const { theme } = useGalleryTheme();

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6" style={{ color: theme.colors.text.primary }}>
        Favorite Photos
      </h2>
      <div className="text-center py-12">
        <Heart size={64} className="mx-auto mb-4" style={{ color: theme.colors.text.muted }} />
        <p style={{ color: theme.colors.text.secondary }}>
          Photos you favorite will be saved here for easy access.
        </p>
      </div>
    </div>
  );
};

// Preferences Tab Component
const PreferencesTab: React.FC<{ customer: any }> = ({ customer }) => {
  const { theme } = useGalleryTheme();

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6" style={{ color: theme.colors.text.primary }}>
        Preferences
      </h2>

      <div className="space-y-6">
        {/* Notification Preferences */}
        <div>
          <h3 className="font-medium mb-4" style={{ color: theme.colors.text.primary }}>
            Email Notifications
          </h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={customer.preferences?.notifications?.newGalleries}
                className="rounded"
                style={{ accentColor: theme.colors.primary }}
              />
              <span style={{ color: theme.colors.text.secondary }}>New gallery notifications</span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={customer.preferences?.notifications?.orderUpdates}
                className="rounded"
                style={{ accentColor: theme.colors.primary }}
              />
              <span style={{ color: theme.colors.text.secondary }}>Order status updates</span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={customer.preferences?.notifications?.promotions}
                className="rounded"
                style={{ accentColor: theme.colors.primary }}
              />
              <span style={{ color: theme.colors.text.secondary }}>Promotional emails</span>
            </label>
          </div>
        </div>

        {/* Privacy Preferences */}
        <div>
          <h3 className="font-medium mb-4" style={{ color: theme.colors.text.primary }}>
            Privacy Settings
          </h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={customer.preferences?.privacy?.showInCustomerDirectory}
                className="rounded"
                style={{ accentColor: theme.colors.primary }}
              />
              <span style={{ color: theme.colors.text.secondary }}>Show in customer directory</span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={customer.preferences?.privacy?.allowPhotographerContact}
                className="rounded"
                style={{ accentColor: theme.colors.primary }}
              />
              <span style={{ color: theme.colors.text.secondary }}>
                Allow photographer to contact me
              </span>
            </label>
          </div>
        </div>

        <Button>Save Preferences</Button>
      </div>
    </div>
  );
};

export default CustomerAccountPage;
